import i18next, { exists, t as i18t, use } from "i18next";
import { initReactI18next } from "react-i18next";

export const LANGUAGES = {
  "da": "Dansk",
  "de": "Deutsch",
  "en": "English",
  "es": "Español",
  "fr": "Français",
  "he": "עברית",
  "it": "Italiano",
  "ja": "日本語",
  "ko": "한국어",
  "nb": "Norsk",
  "nl": "Nederlands",
  "pl": "Polski",
  "pt-BR": "Português Brasileiro",
  "pt-PT": "Português",
  "ru": "Русский",
  "sv": "Svenska",
  "zh-CN": "中文",
} as const;

export function getLanguageLabel(code: keyof typeof LANGUAGES): string {
  const native = LANGUAGES[code];
  if (code === i18next.language) return native;
  return `${native} (${getTranslatedLanguageName(code)})`;
}

// A language's name in the current UI language (e.g. for a Korean UI, "fr" → "프랑스어"), or the raw
// code for languages we don't have (e.g. "it-IT").
export function getTranslatedLanguageName(code: string): string {
  switch (code) {
    case "da":
      return t("Danish");
    case "de":
      return t("German");
    case "en":
      return t("English");
    case "es":
      return t("Spanish");
    case "fr":
      return t("French");
    case "he":
      return t("Hebrew");
    case "it":
      return t("Italian");
    case "ja":
      return t("Japanese");
    case "ko":
      return t("Korean");
    case "nb":
      return t("Norwegian");
    case "nl":
      return t("Dutch");
    case "pl":
      return t("Polish");
    case "pt-BR":
      return t("Brazilian Portuguese");
    case "pt-PT":
      return t("Portuguese");
    case "ru":
      return t("Russian");
    case "sv":
      return t("Swedish");
    case "zh-CN":
      return t("Chinese");
    default:
      return code;
  }
}

export const LOAD_LANGUAGE_FILES: Record<string, () => Promise<Record<string, string>>> = {
  /* eslint-disable @typescript-eslint/no-unsafe-return */
  // @ts-ignore
  "da": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/da.yml"),
  // @ts-ignore
  "de": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/de.yml"),
  // @ts-ignore
  "es": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/es.yml"),
  // @ts-ignore
  "fr": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/fr.yml"),
  // @ts-ignore
  "he": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/he.yml"),
  // @ts-ignore
  "it": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/it.yml"),
  // @ts-ignore
  "ja": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/ja.yml"),
  // @ts-ignore
  "ko": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/ko.yml"),
  // @ts-ignore
  "nb": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/nb.yml"),
  // @ts-ignore
  "nl": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/nl.yml"),
  // @ts-ignore
  "pl": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/pl.yml"),
  // @ts-ignore
  "pt-BR": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/pt-BR.yml"),
  // @ts-ignore
  "pt-PT": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/pt-PT.yml"),
  // @ts-ignore
  "ru": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/ru.yml"),
  // @ts-ignore
  "sv": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/sv.yml"),
  // @ts-ignore
  "zh-CN": async () => import(process.env.STORYBOOK ? "./locales/mock-locale.json" : "./locales/zh-CN.yml"),
  /* eslint-enable @typescript-eslint/no-unsafe-return */
};

type ExtractInterpolationKeys<S extends string> = S extends `${infer _Start}%{${infer Key}}${infer Rest}`
  ? ExtractInterpolationKeys<Rest> | Key
  : never;

type InterpolationOptions<S extends string> = Record<ExtractInterpolationKeys<S>, number | string>;

export async function initI18n(language = "en"): Promise<void> {
  const genericLanguage = language.split("-")[0]!;
  if (!(language in LOAD_LANGUAGE_FILES)) language = genericLanguage in LOAD_LANGUAGE_FILES ? genericLanguage : "en";

  const resources: Record<string, { translation: Record<string, string> }> = {};
  if (language !== "en") resources[language] = { translation: await LOAD_LANGUAGE_FILES[language]!() };

  use(initReactI18next).init({
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
      prefix: "%{",
      suffix: "}",
    },
    lng: language,
    resources,
  });
}

// Overloaded function definitions
export default function t<S extends string>(value: S extends `${string}%{${string}` ? never : S): string;
export default function t<S extends `${string}%{${string}`, T extends InterpolationOptions<S>>(
  value: S,
  options: T,
): string;
export default function t(value: string, options: Record<string, number | string> = {}): string {
  const key = makeKey(value);
  value = stripHints(value);

  if (!exists(key)) {
    return value.replace(/%{(.+?)}/g, (_, interpolationKey: string) =>
      typeof options[interpolationKey] === "number"
        ? options[interpolationKey].toLocaleString()
        : options[interpolationKey]!,
    );
  }

  return i18t(key, options);
}

function hash(str: string): string {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function makeKey(value: string): string {
  const base = value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/(^_|_$)/g, "")
    .replace(/_+/g, "_")
    .slice(0, 20);

  return `t_${base}_${hash(value)}`;
}

function stripHints(value: string): string {
  return value.replace(/\s*\[\[.*?\]\]/g, "");
}
